import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Box, Grid, ListItemText } from '@mui/material';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { IComposableDashboard, IComposableDashboardCollectionsContext } from 'wisestep-api';
import {
  WsDrawer,
  WsColors,
  WsButton,
  WsTypoVariants,
  WsTypography,
  WsIconText,
  getDateUsingFormat,
} from 'wisestep-base';

import { DrawerFooter } from '../../../../components/filters/components/AdvanceFilterFooterBar';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { useComposableDashboardCollectionsContext } from '../../hooks/useComposableDashboardCollectionsContext';
import { useComposableDashboardsContext } from '../../hooks/useComposableDashboardsContext';
import { TabOption } from '../dashboard/AddChartSelectionDrawer';
import StyledButton from '../StyledButton';

interface IAddDashboardsSelectionDrawer {
  openSelectionDrawer: boolean;
  setChartSelectionDrawer: Dispatch<SetStateAction<boolean>>;
  onPreview: (dashboards: IComposableDashboard[]) => void;
  selectedDashboards: IComposableDashboard[];
  setSelectedDashboards: (dashboards: IComposableDashboard[]) => void;
}

const AddDashboardsSelectionDrawer = ({
  openSelectionDrawer,
  setChartSelectionDrawer,
  onPreview,
}: IAddDashboardsSelectionDrawer) => {
  const { dashboards } = useComposableDashboardsContext();
  const { selectedDashboardCollection, setSelectedCollection } =
    useComposableDashboardCollectionsContext() as Required<IComposableDashboardCollectionsContext>;
  const selectedDashboards = selectedDashboardCollection?.dashboards;
  const setSelectedDashboards = (dashboards: IComposableDashboard[]) =>
    setSelectedCollection((prev) => ({ ...prev, dashboards }));
  const handleDashbaordsSelection = (dashboard: IComposableDashboard, remove: boolean) => {
    if (remove) {
      setSelectedDashboards(selectedDashboards.filter(({ id }) => id != dashboard.id));
    } else {
      setSelectedDashboards([...selectedDashboards, dashboard]);
    }
  };
  const isDashboardAdded = useCallback(
    (dashboard: IComposableDashboard) =>
      selectedDashboards.map((d) => d?.id).includes(dashboard?.id),
    [selectedDashboards]
  );

  return (
    <WsDrawer
      title="Select Dashboards"
      iconType="cross"
      open={openSelectionDrawer}
      onClose={() => setChartSelectionDrawer(false)}
      size="small"
      headerSize="medium"
    >
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        <Box pb={8}>
          <Grid container justifyContent="center" mt={0.1} spacing={4}>
            {dashboards?.map((dashboard) => (
              <Grid item xs={11} key={dashboard.id}>
                <TabOption>
                  <ListItemText>
                    <WsTypography cursor="pointer" variant={WsTypoVariants.TITLE} bold>
                      {dashboard.name}
                    </WsTypography>
                    <Grid container direction="row" ml={1.2} mt={0.5}>
                      <Grid item xs={8} my={1}>
                        <WsIconText
                          icon={
                            <DescriptionRoundedIcon color="disabled" sx={{ fontSize: '18px' }} />
                          }
                          text={dashboard.description ?? 'Not Available'}
                          popOverText={dashboard.description ?? 'Not Available'}
                          isTextOverFlow={true}
                          maxWidth={250}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <WsIconText
                          icon={<InsertEmoticonIcon color="disabled" sx={{ fontSize: '18px' }} />}
                          text={`${dashboard.createdByName}`}
                          popOverText="Created By"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <WsIconText
                          icon={<AccessTimeIcon color="disabled" sx={{ fontSize: '18px' }} />}
                          text={`${getDateUsingFormat(dashboard.createdOn, 'DD MMM ddd HH:mm')}`}
                          popOverText="Created On"
                        />
                      </Grid>
                    </Grid>
                  </ListItemText>
                  <StyledButton
                    variant="text"
                    size="small"
                    startIcon={
                      isDashboardAdded(dashboard) ? <RemoveCircleIcon /> : <AddCircleOutlinedIcon />
                    }
                    onClick={() =>
                      handleDashbaordsSelection(dashboard, isDashboardAdded(dashboard))
                    }
                    remove={isDashboardAdded(dashboard) ?? false}
                  >
                    {isDashboardAdded(dashboard) ? 'Remove' : 'Add'}
                  </StyledButton>
                </TabOption>
              </Grid>
            ))}
          </Grid>

          <DrawerFooter>
            <Grid
              item
              xs={7.2}
              py={2}
              px={12}
              display="flex"
              justifyContent="space-between"
              bgcolor={WsColors.BackGround}
            >
              <WsButton
                variant="contained"
                size="large"
                onClick={() => setChartSelectionDrawer(false)}
              >
                Cancel
              </WsButton>
              <WsButton
                variant="contained"
                size="large"
                onClick={() => onPreview(selectedDashboards)}
              >
                Preview
              </WsButton>
            </Grid>
          </DrawerFooter>
        </Box>
      </ErrorBoundary>
    </WsDrawer>
  );
};
export default AddDashboardsSelectionDrawer;
